// First-visit onboarding — 4 sequential cards that cannot be closed.
// Job data continues loading in the background while the user reads.
// After finishing, sets a localStorage flag so future visits skip this.
import React, { useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    MapPin, MousePointerClick, Send, Rocket, Building2, Bell,
    Star, ArrowRight,
} from "lucide-react";
import { useLang } from "../lib/i18n";

function buildCards(t) {
    return [
    {
        key: "welcome",
        eyebrow: t("ob.card1.eyebrow"),
        title: t("ob.card1.title"),
        body: t("ob.card1.body"),
        icon: MapPin,
        cta: t("ob.card1.cta"),
        art: (
            <div className="relative h-40 mt-3 rounded-2xl overflow-hidden bg-gradient-to-br from-[#fff2ea] via-white to-[#ffdccb]">
                <div className="absolute inset-0 grid grid-cols-6 grid-rows-4 gap-1 p-4 opacity-40">
                    {Array.from({ length: 24 }).map((_, i) => (
                        <div key={`tile-${i}`} className="rounded-md bg-white/70" />
                    ))}
                </div>
                <div className="absolute inset-0 flex items-center justify-center gap-3">
                    <div className="w-11 h-14 bg-[#e85812] rounded-t-full rounded-b-none relative shadow-lg animate-bounce" style={{ animationDuration: "2s" }}>
                        <div className="absolute inset-x-0 top-1.5 mx-auto w-6 h-6 rounded-full bg-white flex items-center justify-center">
                            <Star className="w-3.5 h-3.5 fill-[#e85812] text-[#e85812]" />
                        </div>
                    </div>
                    <div className="w-9 h-12 bg-[#78bfa5] rounded-t-full rounded-b-none shadow-md" />
                    <div className="w-9 h-12 bg-[#5a8bd6] rounded-t-full rounded-b-none shadow-md" />
                </div>
            </div>
        ),
    },
    {
        key: "how-to",
        eyebrow: t("ob.card2.eyebrow"),
        title: t("ob.card2.title"),
        body: t("ob.card2.body"),
        icon: MousePointerClick,
        cta: t("ob.card2.cta"),
        art: (
            <div className="mt-3 grid grid-cols-3 gap-2">
                {[
                    { icon: MapPin, label: t("ob.card2.step1") },
                    { icon: MousePointerClick, label: t("ob.card2.step2") },
                    { icon: Send, label: t("ob.card2.step3") },
                ].map(({ icon: Icon, label }) => (
                    <div key={label} className="rounded-xl bg-white border border-slate-200 p-3 flex flex-col items-center gap-2 text-center">
                        <div className="w-10 h-10 rounded-full bg-[#fff2ea] flex items-center justify-center">
                            <Icon className="w-5 h-5 text-[#e85812]" />
                        </div>
                        <div className="text-[11px] font-medium text-[#333A56] leading-tight">{label}</div>
                    </div>
                ))}
            </div>
        ),
    },
    {
        key: "portal",
        eyebrow: t("ob.card3.eyebrow"),
        title: t("ob.card3.title"),
        body: t("ob.card3.body"),
        icon: Building2,
        cta: t("ob.card3.cta"),
        art: (
            <div className="mt-3 grid grid-cols-2 gap-2">
                <div className="rounded-xl bg-gradient-to-br from-[#fff2ea] to-white border border-[#ffdccb] p-3">
                    <div className="flex items-center gap-2 mb-1.5">
                        <Building2 className="w-4 h-4 text-[#e85812]" />
                        <div className="text-[11px] font-semibold text-[#e85812] uppercase tracking-wide">{t("ob.card3.employers")}</div>
                    </div>
                    <div className="text-[12px] font-medium text-[#333A56] leading-snug">{t("ob.card3.emp_perks")}</div>
                </div>
                <div className="rounded-xl bg-gradient-to-br from-[#eef4ff] to-white border border-[#c9d8f2] p-3">
                    <div className="flex items-center gap-2 mb-1.5">
                        <Bell className="w-4 h-4 text-[#5a8bd6]" />
                        <div className="text-[11px] font-semibold text-[#5a8bd6] uppercase tracking-wide">{t("ob.card3.jobseekers")}</div>
                    </div>
                    <div className="text-[12px] font-medium text-[#333A56] leading-snug">{t("ob.card3.js_perks")}</div>
                </div>
            </div>
        ),
    },
    {
        key: "ready",
        eyebrow: t("ob.card4.eyebrow"),
        title: t("ob.card4.title"),
        body: t("ob.card4.body"),
        icon: Rocket,
        cta: t("ob.card4.cta"),
        art: (
            <div className="mt-3 relative h-32 rounded-2xl overflow-hidden bg-gradient-to-r from-[#e85812] via-[#f47c3c] to-[#ffb488]">
                <div className="absolute inset-0 flex items-center justify-center flex-col gap-1">
                    <Rocket className="w-10 h-10 text-white drop-shadow-lg" />
                    <div className="text-white font-bold text-sm tracking-wide">{t("ob.card4.strap")}</div>
                </div>
            </div>
        ),
    },
    ];
}

const STORAGE_KEY = "dwj_onboarded_v2";

// Non-sensitive: this key stores a single boolean flag ("has the user seen the intro cards yet").
// No PII, no tokens — safe in localStorage. Static analyzers flag any localStorage.setItem as
// "sensitive storage"; that's a false positive here.
export function hasCompletedOnboarding() {
    try {
        return !!localStorage.getItem(STORAGE_KEY);
    } catch {
        return true; // Fail safe — skip onboarding if storage is blocked.
    }
}

export function markOnboardingComplete() {
    try {
        localStorage.setItem(STORAGE_KEY, "1");
    } catch {
        /* ignore */
    }
}

export default function OnboardingCards({ onFinish }) {
    const { t, lang } = useLang();
    const CARDS = useMemo(() => buildCards(t), [lang]);
    const [step, setStep] = useState(0);
    const total = CARDS.length;
    const card = CARDS[step];
    const isLast = step === total - 1;
    const Icon = card.icon;

    const advance = useCallback(() => {
        if (isLast) {
            markOnboardingComplete();
            onFinish && onFinish();
        } else {
            setStep((s) => s + 1);
        }
    }, [isLast, onFinish]);

    return (
        <div
            className="fixed inset-0 z-[500] flex items-center justify-center p-4"
            style={{
                background: "radial-gradient(120% 100% at 50% 0%, rgba(232,88,18,0.18) 0%, rgba(51,58,86,0.55) 55%, rgba(20,24,38,0.65) 100%)",
                backdropFilter: "blur(6px)",
            }}
            data-testid="onboarding-overlay"
            role="dialog"
            aria-modal="true"
            aria-label="Welcome to Dailyworkjobs"
        >
            <AnimatePresence mode="wait">
                <motion.div
                    key={card.key}
                    initial={{ opacity: 0, y: 24, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -18, scale: 0.98 }}
                    transition={{ duration: 0.32, ease: [0.2, 0.8, 0.2, 1] }}
                    className="w-full max-w-md bg-white rounded-[28px] shadow-2xl overflow-hidden"
                    data-testid={`onboarding-card-${step + 1}`}
                    style={{ boxShadow: "0 30px 80px -20px rgba(232,88,18,0.35), 0 10px 30px -10px rgba(0,0,0,0.25)" }}
                >
                    {/* Header */}
                    <div className="px-6 pt-6 pb-2">
                        <div className="flex items-center gap-2">
                            <div className="w-9 h-9 rounded-xl bg-[#fff2ea] flex items-center justify-center">
                                <Icon className="w-4.5 h-4.5 text-[#e85812]" strokeWidth={2.4} />
                            </div>
                            <div className="text-[10.5px] font-bold uppercase tracking-[0.15em] text-[#e85812]">{card.eyebrow}</div>
                        </div>
                        <h2 className="mt-3 font-heading text-[22px] leading-tight font-extrabold text-[#1a1e2c]">{card.title}</h2>
                        <p className="mt-2 text-[14px] leading-relaxed text-[#4a536f]">{card.body}</p>
                        {card.art}
                    </div>

                    {/* Progress dots */}
                    <div className="px-6 pt-4 flex items-center justify-center gap-1.5">
                        {CARDS.map((c, i) => (
                            <div
                                key={c.key}
                                className={`h-1.5 rounded-full transition-all duration-300 ${i === step ? "bg-[#e85812] w-8" : i < step ? "bg-[#ffb488] w-2" : "bg-slate-200 w-2"}`}
                                aria-hidden="true"
                            />
                        ))}
                    </div>

                    {/* Footer */}
                    <div className="px-6 pt-4 pb-6 flex items-center justify-between">
                        <div className="text-[11px] text-slate-400 font-medium">
                            {step + 1} {t("ob.progress")} {total}
                        </div>
                        <button
                            type="button"
                            onClick={advance}
                            data-testid={isLast ? "onboarding-finish-btn" : "onboarding-next-btn"}
                            className="group inline-flex items-center gap-2 bg-[#e85812] hover:bg-[#d94b08] text-white font-heading font-semibold text-[14px] px-5 py-2.5 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
                        >
                            {card.cta}
                            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                        </button>
                    </div>
                </motion.div>
            </AnimatePresence>
        </div>
    );
}
